import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { MdDelete, MdEdit, MdSchedule, MdVideoLibrary, MdSearch, MdFilterList } from 'react-icons/md';
import { FaYoutube, FaFacebook, FaTiktok, FaInstagram } from 'react-icons/fa';
import { getVideos, getChannels, deleteVideo, updateVideo } from '../utils/api';

const statusColors = {
  draft: 'badge-secondary',
  scheduled: 'badge-warning',
  published: 'badge-success',
  failed: 'badge-danger'
};

const VideoLibrary = () => {
  const [videos, setVideos] = useState([]);
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterChannel, setFilterChannel] = useState('');
  const [editingVideo, setEditingVideo] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [videosRes, channelsRes] = await Promise.all([
        getVideos(),
        getChannels()
      ]);
      setVideos(videosRes.data.data || []);
      setChannels(channelsRes.data.data || []);
    } catch (error) {
      toast.error('Failed to load videos');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this video?')) return;
    try {
      await deleteVideo(id);
      toast.success('Video deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete video');
    }
  };

  const handleEdit = (video) => {
    setEditingVideo({ ...video });
    setShowModal(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateVideo(editingVideo.id, editingVideo);
      toast.success('Video updated successfully');
      setShowModal(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to update video');
    } finally {
      setSaving(false);
    }
  };

  const getChannelName = (channelId) => {
    const channel = channels.find(c => c.id === channelId);
    return channel ? channel.name : 'Unknown';
  };

  const getChannelPlatform = (channelId) => {
    const channel = channels.find(c => c.id === channelId);
    return channel ? channel.platform : '';
  };

  const platformIcons = {
    youtube: <FaYoutube style={{ color: '#ff0000', fontSize: '16px' }} />,
    facebook: <FaFacebook style={{ color: '#1877f2', fontSize: '16px' }} />,
    tiktok: <FaTiktok style={{ color: '#000000', fontSize: '16px' }} />,
    instagram: <FaInstagram style={{ color: '#e1306c', fontSize: '16px' }} />
  };

  const filteredVideos = videos.filter(video => {
    const matchSearch = video.title?.toLowerCase().includes(search.toLowerCase()) ||
      video.youtube_title?.toLowerCase().includes(search.toLowerCase());
    const matchStatus = filterStatus ? video.status === filterStatus : true;
    const matchChannel = filterChannel ? video.channel_id === parseInt(filterChannel) : true;
    return matchSearch && matchStatus && matchChannel;
  });

  if (loading) {
    return (
      <div className="page-container">
        <div className="loading"><div className="spinner"></div>Loading videos...</div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h1>Video Library</h1>
            <p>Manage all your uploaded videos</p>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <span className="badge badge-info">{videos.length} Total Videos</span>
          </div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: '200px', position: 'relative' }}>
            <MdSearch style={{
              position: 'absolute', left: '12px', top: '50%',
              transform: 'translateY(-50%)', color: '#94a3b8', fontSize: '18px'
            }} />
            <input
              className="form-control"
              style={{ paddingLeft: '36px' }}
              placeholder="Search videos..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <select
            className="form-select"
            style={{ width: '160px' }}
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="">All Status</option>
            <option value="draft">Draft</option>
            <option value="scheduled">Scheduled</option>
            <option value="published">Published</option>
            <option value="failed">Failed</option>
          </select>
          <select
            className="form-select"
            style={{ width: '180px' }}
            value={filterChannel}
            onChange={(e) => setFilterChannel(e.target.value)}
          >
            <option value="">All Channels</option>
            {channels.map(ch => (
              <option key={ch.id} value={ch.id}>{ch.name}</option>
            ))}
          </select>
        </div>
      </div>

      {filteredVideos.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <MdVideoLibrary style={{ fontSize: '48px', color: '#c7d2fe', marginBottom: '12px' }} />
            <h3>No videos found</h3>
            <p>Upload your first video to get started</p>
          </div>
        </div>
      ) : (
        <div className="card">
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Video</th>
                  <th>Channel</th>
                  <th>Platform</th>
                  <th>Status</th>
                  <th>AI Used</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredVideos.map((video) => (
                  <tr key={video.id}>
                    <td>
                      <div>
                        <div style={{ fontWeight: '500', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {video.youtube_title || video.title || 'Untitled'}
                        </div>
                        {video.tags && (
                          <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '2px' }}>
                            {video.tags.split(',').slice(0, 3).map(tag => (
                              <span key={tag} style={{
                                background: '#f1f5f9', padding: '1px 6px',
                                borderRadius: '4px', marginRight: '4px', fontSize: '11px'
                              }}>
                                {tag.trim()}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </td>
                    <td style={{ fontSize: '13px' }}>{getChannelName(video.channel_id)}</td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        {platformIcons[getChannelPlatform(video.channel_id)]}
                        <span style={{ fontSize: '13px', textTransform: 'capitalize' }}>
                          {getChannelPlatform(video.channel_id)}
                        </span>
                      </div>
                    </td>
                    <td>
                      <span className={`badge ${statusColors[video.status]}`}>
                        {video.status}
                      </span>
                    </td>
                    <td>
                      {video.claude_ai_used ? (
                        <span className="ai-badge">🤖 AI</span>
                      ) : (
                        <span style={{ color: '#94a3b8', fontSize: '13px' }}>Manual</span>
                      )}
                    </td>
                    <td style={{ fontSize: '13px', color: '#64748b' }}>
                      {new Date(video.created_at).toLocaleDateString()}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => handleEdit(video)}
                          title="Edit"
                        >
                          <MdEdit />
                        </button>
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={() => handleDelete(video.id)}
                          title="Delete"
                        >
                          <MdDelete />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showModal && editingVideo && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" style={{ maxWidth: '680px' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Edit Video Metadata</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>✕</button>
            </div>

            <div className="form-group">
              <label className="form-label">General Title</label>
              <input
                className="form-control"
                value={editingVideo.title || ''}
                onChange={(e) => setEditingVideo({ ...editingVideo, title: e.target.value })}
              />
            </div>

            <div style={{ borderBottom: '1px solid #e2e8f0', margin: '16px 0', paddingBottom: '8px' }}>
              <span style={{ fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FaYoutube style={{ color: '#ff0000' }} /> YouTube
              </span>
            </div>
            <div className="form-group">
              <label className="form-label">YouTube Title</label>
              <input
                className="form-control"
                maxLength={100}
                value={editingVideo.youtube_title || ''}
                onChange={(e) => setEditingVideo({ ...editingVideo, youtube_title: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label className="form-label">YouTube Description</label>
              <textarea
                className="form-control"
                rows={4}
                value={editingVideo.youtube_description || ''}
                onChange={(e) => setEditingVideo({ ...editingVideo, youtube_description: e.target.value })}
              />
            </div>

            <div style={{ borderBottom: '1px solid #e2e8f0', margin: '16px 0', paddingBottom: '8px' }}>
              <span style={{ fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FaFacebook style={{ color: '#1877f2' }} /> Facebook
              </span>
            </div>
            <div className="form-group">
              <label className="form-label">Facebook Title</label>
              <input
                className="form-control"
                value={editingVideo.facebook_title || ''}
                onChange={(e) => setEditingVideo({ ...editingVideo, facebook_title: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Facebook Description</label>
              <textarea
                className="form-control"
                rows={3}
                value={editingVideo.facebook_description || ''}
                onChange={(e) => setEditingVideo({ ...editingVideo, facebook_description: e.target.value })}
              />
            </div>

            <div style={{ borderBottom: '1px solid #e2e8f0', margin: '16px 0', paddingBottom: '8px' }}>
              <span style={{ fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FaTiktok /> TikTok & <FaInstagram style={{ color: '#e1306c' }} /> Instagram
              </span>
            </div>
            <div className="grid-2">
              <div className="form-group">
                <label className="form-label">TikTok Caption</label>
                <input
                  className="form-control"
                  maxLength={150}
                  value={editingVideo.tiktok_title || ''}
                  onChange={(e) => setEditingVideo({ ...editingVideo, tiktok_title: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Instagram Caption</label>
                <input
                  className="form-control"
                  maxLength={150}
                  value={editingVideo.instagram_title || ''}
                  onChange={(e) => setEditingVideo({ ...editingVideo, instagram_title: e.target.value })}
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Status</label>
              <select
                className="form-select"
                value={editingVideo.status}
                onChange={(e) => setEditingVideo({ ...editingVideo, status: e.target.value })}
              >
                <option value="draft">Draft</option>
                <option value="scheduled">Scheduled</option>
                <option value="published">Published</option>
                <option value="failed">Failed</option>
              </select>
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '8px' }}>
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoLibrary;